import { ONBOARDING_STEP_STATUS_LABELS, type OnboardingStepStatus } from '@lia/contracts';
import { Alert02Icon, CheckmarkCircle02Icon, CircleIcon, Clock01Icon, MinusSignCircleIcon } from '@hugeicons/core-free-icons';
import * as React from 'react';
import { cn } from '../../lib/cn.js';
import { StatusBadge, type StatusTone } from '../primitives/badge.js';
import { Icon, type IconData } from '../primitives/icon.js';
import { ProgressBar } from '../primitives/progress.js';
import { Skeleton } from '../primitives/states.js';

export const ONBOARDING_STATUS_TONE: Record<OnboardingStepStatus, StatusTone> = {
  done: 'success',
  pending: 'neutral',
  blocked: 'warning',
  not_applicable: 'neutral',
  unavailable: 'info',
};

const ONBOARDING_STATUS_ICON: Record<OnboardingStepStatus, IconData> = {
  done: CheckmarkCircle02Icon,
  pending: CircleIcon,
  blocked: Alert02Icon,
  not_applicable: MinusSignCircleIcon,
  unavailable: Clock01Icon,
};

/** Status chip for an onboarding step: color + icon + text label (never color alone). */
export function OnboardingStatusBadge({ status }: { status: OnboardingStepStatus }) {
  return (
    <StatusBadge tone={ONBOARDING_STATUS_TONE[status]} icon={<Icon icon={ONBOARDING_STATUS_ICON[status]} aria-hidden />}>
      {ONBOARDING_STEP_STATUS_LABELS[status]}
    </StatusBadge>
  );
}

export interface OnboardingStepItem {
  key: string;
  title: string;
  description: string;
  status: OnboardingStepStatus;
  /** Optional steps are informative and do not count toward progress. */
  required: boolean;
  /** Why the step is not done / does not apply. Shown as «Por qué: …». */
  reason?: string | null;
  /** Fact from the real state when done (e.g. «2 proyectos»). */
  detail?: string | null;
  /** Who completes the step, shown when the viewer cannot do it themselves. */
  actorLabel?: string | null;
  /** Link or button to the screen where the step is completed. */
  action?: React.ReactNode;
}

export interface OnboardingStepListProps {
  steps: readonly OnboardingStepItem[];
  /** Required steps done (or not applicable). */
  completed: number;
  requiredTotal: number;
  title?: string;
  loading?: boolean;
  className?: string;
}

/** Ordered per-module setup steps with server-derived status, progress over required steps and per-step actions. */
export function OnboardingStepList({ steps, completed, requiredTotal, title = 'Pasos', loading, className }: OnboardingStepListProps) {
  const titleId = React.useId();
  const pct = requiredTotal ? (completed / requiredTotal) * 100 : 0;
  return (
    <section aria-labelledby={titleId} className={cn('grid gap-4', className)}>
      <div className="grid gap-2">
        <h2 id={titleId} className="text-body-lg font-semibold text-text-primary">
          {title}
        </h2>
        {loading ? (
          <Skeleton className="h-2 w-full" />
        ) : (
          <ProgressBar value={pct} label="Pasos obligatorios completos" valueText={`${completed} de ${requiredTotal} obligatorios`} />
        )}
      </div>
      {loading ? (
        <div role="status" aria-label="Cargando pasos" className="grid gap-3">
          {Array.from({ length: 4 }, (_, i) => (
            <Skeleton key={i} className="h-16 w-full" />
          ))}
        </div>
      ) : (
        <ol className="grid divide-y divide-border-subtle rounded-card border border-border-subtle bg-surface-default">
          {steps.map((s, i) => (
            <li key={s.key} className="grid gap-2 px-4 py-3">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-body font-medium text-text-primary">
                  <span className="ef-tabular text-text-secondary">{i + 1}. </span>
                  {s.title}
                </span>
                <OnboardingStatusBadge status={s.status} />
                {!s.required ? <span className="text-meta text-text-secondary">Opcional</span> : null}
              </div>
              <p className="text-meta text-text-secondary">{s.description}</p>
              {s.status === 'done' && s.detail ? <p className="text-meta text-text-primary">{s.detail}</p> : null}
              {s.status !== 'done' && s.reason ? (
                <p className="text-meta text-text-primary">
                  <span className="font-medium">Por qué: </span>
                  {s.reason}
                </p>
              ) : null}
              {s.status === 'done' && s.reason && !s.detail ? <p className="text-meta text-text-primary">{s.reason}</p> : null}
              {s.actorLabel ? (
                <p className="text-meta text-text-secondary">
                  <span className="font-medium">Quién: </span>
                  {s.actorLabel}
                </p>
              ) : null}
              {s.action ? <div className="flex flex-wrap gap-2">{s.action}</div> : null}
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}
